import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { Button, LoadingIndicator } from 'components/common';
import { TextInput } from 'components/common/form/common';
import {
  ProfileCompetencyMappingChatText,
  ProfileCompetencyMappingHeader
} from '.';

import type { KeyboardEvent } from 'react';
import type { SendCompetencyMappingMessageRes } from 'shared-types/responses';
import type {
  ChatMessage,
  CompetencyMappingState
} from './ProfileCompetencyMapping';

interface Props {
  setState: Dispatch<SetStateAction<CompetencyMappingState>>;
  state: CompetencyMappingState;
}

const ProfileCompetencyMappingChat = ({ setState, state }: Props) => {
  const { dispatch } = useDispatch();
  const { messages, theme } = useReduxState();

  const [draftMessage, setDraftMessage] = useState('');
  const messageIdRef = useRef(0);
  const hasInitializedRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // create a local chat message with unique id
  const createChatMessage = (
    sender: ChatMessage['sender'],
    text: string
  ): ChatMessage => {
    messageIdRef.current += 1;

    return {
      id: `chat-message-${messageIdRef.current}`,
      sender,
      text
    };
  };

  // send conversation to the competency mapping backend
  const requestCompetencyMappingMessage = async (
    chatMessages: ChatMessage[]
  ) => {
    const { experienceYears } = state.roleDetails;
    const focusAreas = state.roleDetails.focusAreas?.trim();
    const jobTitle = state.roleDetails.jobTitle.trim();

    const { data } = await api.post<SendCompetencyMappingMessageRes>(
      '/admin/competency-mapping/message',
      {
        context: {
          experienceYears,
          ...(focusAreas ? { focusAreas } : {}),
          jobTitle
        },
        messages: chatMessages.map(({ sender, text }) => ({
          role: sender === 'assistant' ? 'model' : 'user',
          text
        }))
      }
    );

    return data;
  };

  useEffect(() => {
    if (hasInitializedRef.current) return;

    hasInitializedRef.current = true;

    const initializeConversation = async () => {
      try {
        setState({ ...state, isWaitingForReply: true });

        const data = await requestCompetencyMappingMessage([]);

        setState({
          ...state,
          chatMessages: [createChatMessage('assistant', data.message)],
          isConversationComplete: data.conversationComplete,
          isWaitingForReply: false
        });
      } catch (err) {
        await dispatch.handleErrorDispatch(err);
        setState({ ...state, isWaitingForReply: false });
      }
    };

    initializeConversation();
  }, []);

  const scrollIntoView = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(
    () => scrollIntoView,
    [state.chatMessages, state.isWaitingForReply]
  );

  // add the user's message to the chat and request the next AI reply
  const handleSendMessage = async () => {
    try {
      if (state.isWaitingForReply) return;

      const trimmedMessage = draftMessage.trim();
      if (!trimmedMessage) return;

      const nextMessages = [
        ...state.chatMessages,
        createChatMessage('user', trimmedMessage)
      ];

      setState({
        ...state,
        chatMessages: nextMessages,
        isWaitingForReply: true
      });
      setDraftMessage('');

      const data = await requestCompetencyMappingMessage(nextMessages);

      setState((prevState) => ({
        ...state,
        chatMessages: [
          ...prevState.chatMessages,
          createChatMessage('assistant', data.message)
        ],
        isConversationComplete: data.conversationComplete,
        isWaitingForReply: false
      }));
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
      setState({ ...state, isWaitingForReply: false });
    }
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    void handleSendMessage();
  };

  const isChatReady = Boolean(state.chatMessages.length);
  const isConversationInitializing = state.isWaitingForReply && !isChatReady;
  const isInputDisabled =
    state.isConversationComplete || state.isAnalyzing || !isChatReady;
  const statusText = state.isWaitingForReply
    ? isChatReady
      ? messages.competencyMappingAssistantIsTyping
      : null
    : state.isConversationComplete
    ? messages.competencyMappingAssistantHasEnoughInfo
    : null;

  return (
    <div className="competency-mapping-chat">
      <ProfileCompetencyMappingHeader
        description={messages.competencyMappingDiscussionInfo}
        title={messages.competencyMappingDiscussion}
      />

      <div className="chat-messages">
        {isConversationInitializing ? (
          <div className="chat-loader">
            <LoadingIndicator padding={0} />
          </div>
        ) : null}
        {state.chatMessages.map(({ id, sender, text }, messageIndex) => (
          <div
            className={`message-row ${
              sender === 'user' ? 'is-user' : 'is-assistant'
            }`}
            key={id}
          >
            <div className={`message-bubble ${sender}`}>
              <ProfileCompetencyMappingChatText
                onScrollIntoView={scrollIntoView}
                text={text}
                useTypewriterEffect={
                  sender === 'assistant' &&
                  messageIndex === state.chatMessages.length - 1
                }
              />
            </div>
          </div>
        ))}

        {statusText ? <div className="chat-status">{statusText}</div> : null}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-footer">
        <div className="chat-input">
          <TextInput
            aria-label={messages.competencyMappingChatMessage}
            disabled={isInputDisabled}
            onChange={(event) => setDraftMessage(event.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={
              isInputDisabled && !isChatReady
                ? messages.competencyMappingPreparingConversation
                : messages.competencyMappingWriteMessage
            }
            style={{ padding: '10px 15px' }}
            type="text"
            value={draftMessage}
          />
        </div>

        <div className="chat-action">
          <Button
            category="default"
            disabled={!draftMessage.trim() || isInputDisabled}
            icon="paper-plane"
            isRounded={true}
            onClick={handleSendMessage}
            size="large"
            tooltip={messages.usersSendMessage}
            type="button"
          />
        </div>
      </div>

      <style jsx>{`
        .competency-mapping-chat {
          background-color: white;
          display: flex;
          flex-direction: column;
          height: min(72vh, 640px);
          min-height: 520px;
        }
        .chat-messages {
          display: flex;
          flex: 1;
          flex-direction: column;
          gap: 15px;
          overflow-y: auto;
          padding: 15px;
        }
        .chat-loader {
          align-items: center;
          display: flex;
          flex: 1;
          justify-content: center;
          min-height: 160px;
        }
        .message-row {
          display: flex;
          width: 100%;
        }
        .message-row.is-user {
          justify-content: flex-end;
        }
        .message-bubble {
          border-radius: ${theme.cardBorderRadius};
          font-size: 1.6rem;
          font-weight: 400;
          line-height: 1.6;
          max-width: min(85%, 760px);
          padding: 12px 18px;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .chat-status {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          margin-top: auto;
          padding: 10px;
          text-align: center;
        }
        .message-bubble.assistant {
          background-color: ${theme.lightGreyLighter};
          color: ${theme.textColor};
        }
        .message-bubble.user {
          background-color: ${theme.mainColor};
          color: ${theme.mainTextColor};
        }
        .chat-footer {
          align-items: center;
          border-top: 1px solid ${theme.lightGreyDark};
          display: flex;
          gap: 10px;
          padding: 15px;
        }
        .chat-input {
          flex-grow: 1;
          flex-shrink: 1;
          min-width: 0;
        }
        .chat-action {
          flex-grow: 0;
          flex-shrink: 0;
        }
        @media (max-width: 800px) {
          .competency-mapping-chat {
            height: min(78vh, 640px);
            min-height: 480px;
          }
          .message-bubble {
            max-width: 92%;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileCompetencyMappingChat;
